import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MENU } from "../lib/data";
import { useT } from "../lib/i18n";
import { MustacheMark } from "./MustacheMark";

export const MenuSection = () => {
  const { t, lang } = useT();
  const [active, setActive] = useState(0);
  const item = MENU[active];

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative bg-black text-white py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col items-start md:items-center text-left md:text-center mb-16 md:mb-24">
          <span className="text-[11px] tracking-[0.4em] uppercase text-gold">
            {t("menu.kicker")}
          </span>
          <h2
            data-testid="menu-heading"
            className="mt-4 font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
          >
            {t("menu.heading.1")}{" "}
            <span className="italic text-gold">{t("menu.heading.2")}</span>
          </h2>
          <p className="mt-6 max-w-2xl text-white/60 font-light text-base md:text-lg leading-relaxed">
            {t("menu.intro")}
          </p>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-16">
          <div
            data-testid="menu-list"
            className="col-span-12 lg:col-span-7 order-2 lg:order-1"
          >
            <ul className="border-t border-gold/15">
              {MENU.map((m, i) => {
                const isActive = i === active;
                return (
                  <li
                    key={m.id}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => setActive(i)}
                    className="border-b border-gold/15"
                  >
                    <button
                      data-testid={`menu-item-${m.id}`}
                      className="w-full text-left py-7 md:py-9 flex items-baseline gap-6 group transition-colors"
                    >
                      <span
                        className={`font-display italic text-sm md:text-base shrink-0 transition-colors ${
                          isActive ? "text-gold" : "text-white/30"
                        }`}
                      >
                        0{i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-display text-2xl md:text-4xl tracking-tight transition-colors ${
                            isActive ? "text-gold" : "text-white"
                          }`}
                        >
                          {m.name}
                          <span className="ml-3 text-sm md:text-base font-body font-light tracking-wide text-white/50">
                            — {m.subtitle[lang]}
                          </span>
                        </div>
                        <AnimatePresence initial={false}>
                          {isActive && (
                            <motion.p
                              initial={{ opacity: 0, height: 0, marginTop: 0 }}
                              animate={{ opacity: 1, height: "auto", marginTop: 12 }}
                              exit={{ opacity: 0, height: 0, marginTop: 0 }}
                              transition={{ duration: 0.4, ease: "easeOut" }}
                              className="text-white/65 text-sm md:text-base font-light leading-relaxed max-w-xl"
                            >
                              {m.description[lang]}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </div>
                      <span
                        className={`hidden font-display text-lg md:text-2xl shrink-0 transition-colors ${
                          isActive ? "text-gold" : "text-white/40"
                        }`}
                      >
                        {lang === "fr" ? m.priceEUR : m.price}
                        <span className="text-xs ml-1 align-top tracking-widest">
                          {lang === "fr" ? "€" : "AED"}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="hidden mt-10 flex items-center gap-3 text-white/40 text-[10px] tracking-[0.35em] uppercase">
              <MustacheMark className="h-4 w-auto" color="#D4A857" />
              <span>{t("menu.footnote")}</span>
            </div>
          </div>

          <div
            className="col-span-12 lg:col-span-5 order-1 lg:order-2"
            data-testid="menu-preview"
          >
            <div className="lg:sticky lg:top-28">
              <div className="relative aspect-[4/5] overflow-hidden bg-black/40 ring-1 ring-inset ring-gold/15">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={item.id}
                    src={item.image}
                    alt={item.name}
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.02 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-black/0" />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80">
                    {item.subtitle[lang]}
                  </div>
                  <div className="mt-1 font-display italic text-2xl md:text-3xl">
                    {item.name}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MenuSection;
